import { Logger } from '@suntex.core/logging';

const logger = Logger.for('@suntex.core/audio');
const buffers = new Map<string, AudioBuffer>();
// Latest requested src per id; loads for any other src are stale.
const sources = new Map<string, string>();
const loads = new Map<string, Promise<void>>();
const activeSources = new Set<AudioBufferSourceNode>();

let context: AudioContext | null = null;
let masterGain: GainNode | null = null;
let muted = false;

function getContext(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  if (!context) {
    context = new AudioContext();
    masterGain = context.createGain();
    masterGain.gain.value = muted ? 0 : 1;
    masterGain.connect(context.destination);
  }
  return context;
}

export function loadSound(src: string, id: string): void {
  const ctx = getContext();
  if (!ctx || sources.get(id) === src) return;

  sources.set(id, src);
  buffers.delete(id);
  const load: Promise<void> = fetch(src.replace(/ /g, '%20'))
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to fetch sound: ${src}`);
      return res.arrayBuffer();
    })
    .then((buf) => ctx.decodeAudioData(buf))
    .then((audioBuffer) => {
      if (sources.get(id) === src) buffers.set(id, audioBuffer);
    })
    .catch((e) => {
      if (sources.get(id) === src) sources.delete(id);
      logger.for(['loadSound']).warn(`Failed to load sound.`, { id, src }, e);
    })
    .finally(() => {
      if (loads.get(id) === load) loads.delete(id);
    });
  loads.set(id, load);
}

/**
 * - `immediate`: plays now, alongside anything else. Never waits for, or
 *   delays, the queue.
 * - `sequential`: joins a single global queue and plays once every earlier
 *   queued sound has finished.
 * - `interrupt`: stops the queued sound that is playing, drops any still
 *   waiting, and becomes the new head of the queue. Immediate sounds keep
 *   playing.
 */
export type PlaySoundMode = 'immediate' | 'sequential' | 'interrupt';

export interface PlaySoundOptions {
  mode?: PlaySoundMode;
}

interface QueuedSound {
  id: string;
  volume: number;
}

const queue: QueuedSound[] = [];
let queueSource: AudioBufferSourceNode | null = null;
// The head of the queue is loading or playing.
let queueBusy = false;
// Bumped whenever the queue is cleared, so a head still waiting on its load
// (or a stopped head's late `ended`) cannot act on the cleared queue.
let queueGeneration = 0;
// Bumped by stopAllSounds so immediate sounds still loading are dropped.
let stopGeneration = 0;
const idleCallbacks: (() => void)[] = [];

/** Runs `callback` once the queue is idle, or never if `stopAllSounds` runs first. */
export function whenSoundQueueIsIdle(callback: () => void): void {
  if (!queueBusy) callback();
  else idleCallbacks.push(callback);
}

function flushIdleCallbacks(): void {
  const callbacks = idleCallbacks.slice();
  idleCallbacks.length = 0;
  for (const callback of callbacks) callback();
}

/**
 * Plays a loaded sound, waiting first for an in-flight load of the same id.
 * Sounds that are muted or not loaded are skipped.
 */
export function playSound(
  id: string,
  volume = 1,
  { mode = 'immediate' }: PlaySoundOptions = {},
): void {
  if (mode === 'immediate') {
    const generation = stopGeneration;
    whenLoaded(id, () => {
      if (generation === stopGeneration) startSound(id, volume);
    });
    return;
  }
  if (mode === 'interrupt') clearQueue();
  queue.push({ id, volume });
  if (!queueBusy) playNextQueued();
}

function whenLoaded(id: string, run: () => void): void {
  const pending = loads.get(id);
  if (pending) void pending.then(run);
  else run();
}

function playNextQueued(): void {
  const next = queue.shift();
  if (!next) {
    queueBusy = false;
    flushIdleCallbacks();
    return;
  }
  queueBusy = true;
  const generation = queueGeneration;
  whenLoaded(next.id, () => {
    if (generation !== queueGeneration) return;
    queueSource = startSound(next.id, next.volume, () => {
      if (generation !== queueGeneration) return;
      queueSource = null;
      playNextQueued();
    });
    if (!queueSource) playNextQueued();
  });
}

function clearQueue(): void {
  queueGeneration++;
  queue.length = 0;
  queueBusy = false;
  if (queueSource) {
    stopSource(queueSource);
    queueSource = null;
  }
}

function stopSource(source: AudioBufferSourceNode): void {
  try {
    source.stop();
  } catch {
    // Already stopped or ended; nothing to do.
  }
  activeSources.delete(source);
}

/** Starts a clip, returning `null` when nothing plays. */
function startSound(
  id: string,
  volume: number,
  onEnded?: () => void,
): AudioBufferSourceNode | null {
  if (muted) return null;
  const ctx = getContext();
  if (!ctx || !masterGain) return null;
  const buffer = buffers.get(id);
  if (!buffer) return null;

  // iOS Safari can report a non-standard 'interrupted' state.
  if (ctx.state !== 'running') {
    void ctx.resume().catch((e) => {
      logger
        .for(['playSound'])
        .warn(`Failed to resume audio context.`, { soundId: id }, e);
    });
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.value = volume;
  source.connect(gain);
  gain.connect(masterGain);

  activeSources.add(source);
  source.addEventListener('ended', () => {
    activeSources.delete(source);
    onEnded?.();
  });
  source.start();
  return source;
}

/** Stops every playing sound and drops anything queued. */
export function stopAllSounds(): void {
  stopGeneration++;
  idleCallbacks.length = 0;
  clearQueue();
  for (const source of activeSources) {
    stopSource(source);
  }
}

export function mute(): void {
  muted = true;
  if (masterGain) masterGain.gain.value = 0;
}

export function unmute(): void {
  muted = false;
  if (masterGain) masterGain.gain.value = 1;
}

type ContextGetter = () => AudioContext | null | undefined;

const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const;
const pendingUnlocks = new Set<ContextGetter>();

/**
 * Resume the context returned by `getContext` on the next user gesture.
 * Repeat calls with the same getter are ignored until the unlock succeeds.
 */
export function unlockOnGesture(getContext: ContextGetter): void {
  if (typeof document === 'undefined' || pendingUnlocks.has(getContext)) {
    return;
  }
  pendingUnlocks.add(getContext);

  function removeListeners(): void {
    for (const type of UNLOCK_EVENTS) {
      document.removeEventListener(type, handleGesture, true);
    }
    pendingUnlocks.delete(getContext);
  }

  function handleGesture(): void {
    const ctx = getContext();
    if (!ctx) return;
    if (ctx.state === 'running') {
      removeListeners();
      return;
    }
    ctx.resume().then(
      () => {
        if ((ctx.state as string) === 'running') removeListeners();
      },
      (e) => {
        logger
          .for(['handleGesture'])
          .warn(`Failed to resume audio context.`, {}, e);
      },
    );
  }

  for (const type of UNLOCK_EVENTS) {
    document.addEventListener(type, handleGesture, true);
  }
}

export function enableAudioUnlock(): void {
  if (typeof AudioContext === 'undefined') return;
  unlockOnGesture(getContext);
}
